/**
 * Exact product-type matching for listings.
 *
 * Shopify's `product_type:` filter matches on contained words, not the whole
 * value. Measured against the live Storefront API (2026-10-14, in stock and
 * priced):
 *   product_type:Fireplaces → 99 = Fireplaces 35 + Outdoor Fireplaces and Firepits 64
 *   product_type:Benches    → 265 = Benches 150 + Shower Benches 107 + Outdoor Benches 8
 *   product_type:Cabinets   → 514 = Cabinets 152 + Pantry Cabinets 356 + Filing Cabinets 6
 * so the Fireplaces page opened with 40 outdoor fire pits and its count never
 * matched its grid. Negation IS honoured on the `products` connection:
 *   product_type:"Fireplaces" AND NOT product_type:"Outdoor Fireplaces and Firepits" → 35
 * The look-alike types are worked out from the build catalogue by
 * scripts/generate-category-stock.mjs.
 */

import * as categoryStock from "@/data/categoryStock";
import type { FacetProduct } from "@/lib/shopify";
import { correctedType } from "../../scripts/type-overrides.mjs";

// Read through the namespace: both tables are written at prebuild, and a
// checkout whose generated file predates them must still build. Without them
// every type weighs the same and only keepExactTypes guards a listing.
const generated = categoryStock as {
  TYPE_STOCK?: Record<string, number>;
  TYPE_EXCLUSIONS?: Record<string, string[]>;
};
const TYPE_STOCK = generated.TYPE_STOCK ?? {};
const TYPE_EXCLUSIONS = generated.TYPE_EXCLUSIONS ?? {};

const key = (type: string) => type.trim().toLowerCase();

/** Build-time in-stock count for a product type; undefined when it was not measured. */
export function typeStock(type: string): number | undefined {
  return TYPE_STOCK[key(type)];
}

/** A query clause that matches this product type and none that merely contain its words. */
export function exactTypeClause(type: string): string {
  const clean = type.replace(/"/g, "");
  const others = TYPE_EXCLUSIONS[key(clean)] ?? [];
  if (others.length === 0) return `product_type:"${clean}"`;
  return `(product_type:"${clean}"${others.map((o) => ` AND NOT product_type:"${o.replace(/"/g, "")}"`).join("")})`;
}

/** A category query with every `product_type:` term made exact. */
export function exactTypeQuery(query: string): string {
  return query.replace(/product_type:(?:"([^"]+)"|([^\s()"]+))/g, (term, quoted?: string, bare?: string) =>
    TYPE_EXCLUSIONS[key(quoted ?? bare ?? "")]?.length ? exactTypeClause(quoted ?? bare ?? "") : term,
  );
}

/**
 * Safety net behind exactTypeClause: keep only products whose type is one of
 * `types`. Catches a look-alike type the supplier adds after the build, and a
 * product the supplier files under a type it is not (scripts/type-overrides.mjs).
 */
export function keepExactTypes<T extends FacetProduct>(products: T[], types: string[]): T[] {
  const wanted = new Set(types.map(key));
  return products.filter((p) => wanted.has(key(correctedType(p.node.title, p.node.productType) || "")));
}
